import { useEffect, useState } from "react";
import { Send, Loader2, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const mono = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';
const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

/**
 * Admin dashboard tile · Vinci Payout Engine at a glance.
 *
 * Shows pending withdrawal count, total held, dispatched-today,
 * and a red SLA halo if the oldest pending request is > 24 hours old.
 * Click → routes to `/admin?tab=wallet` with the Payouts tab active.
 */
export function AdminPayoutsTile() {
  const { api } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/payouts/summary");
        if (mounted) setD(data);
      } catch { /* silent */ }
      finally { if (mounted) setLoading(false); }
    };
    load();
    const iv = setInterval(load, 45000);
    return () => { mounted = false; clearInterval(iv); };
  }, [api]);

  const pending = d?.pending_count ?? 0;
  const held = d?.pending_amount_held ?? 0;
  const dispTodayN = d?.dispatched_today_count ?? 0;
  const dispTodayAmt = d?.dispatched_today_amount ?? 0;
  const oldestHours = d?.oldest_pending_hours ?? 0;
  const active = pending > 0;
  const sla = oldestHours > 24;

  const border = sla   ? "border-[#C0392B]/70 bg-[#C0392B]/[0.08] hover:border-[#C0392B]"
                     : active ? "border-[#D8B23E]/60 bg-[#D8B23E]/[0.06] hover:border-[#D8B23E]"
                              : "border-white/10 bg-black/40 hover:border-[#C9A961]/40";
  const accent = sla ? "#C0392B" : active ? "#D8B23E" : "#8A8A82";

  return (
    <button type="button"
             onClick={() => nav("/admin?tab=wallet")}
             data-testid="admin-payouts-tile"
             className={`w-full text-left rounded-sm border p-6 md:p-7 mb-4 transition-colors flex items-center justify-between gap-6 ${border}`}>
      <div className="min-w-0">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] inline-flex items-center gap-2"
              style={{ color: accent }}>
          <Send size={11} /> Vinci Payout Engine · Withdrawals Queue
        </div>
        <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">
          {loading
            ? "Loading queue…"
            : active
              ? <>{pending} withdrawal{pending === 1 ? "" : "s"} pending · <span style={{ color: accent }}>{inr(held)} held</span></>
              : "No withdrawals in queue"}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 font-body text-[12px] text-[#C9C9C2]">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2FB999]" />
            <span className="text-[#F5F5F0] tabular-nums">{dispTodayN}</span>
            <span className="text-[#8A8A82]">dispatched today</span>
            {dispTodayAmt > 0 && <span className="text-[#8A8A82]">· {inr(dispTodayAmt)}</span>}
          </span>
          {oldestHours > 0 && (
            <span className="inline-flex items-center gap-1.5"
                  style={{ color: sla ? "#C0392B" : "#8A8A82" }}
                  data-testid="admin-payouts-tile-oldest">
              <Clock size={11} />
              Oldest request · {oldestHours}h ago
              {sla && <span className="ml-1 font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#C0392B]">SLA breach</span>}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <div className="text-right">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Pending</div>
          <div className="font-serif text-5xl tabular-nums leading-none mt-1"
                style={{ color: accent, fontFamily: mono }}
                data-testid="admin-payouts-tile-count">
            {loading ? <Loader2 size={28} className="animate-spin" /> : String(pending).padStart(2, "0")}
          </div>
        </div>
        <span className={`hidden md:inline-flex items-center rounded-full border px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] ${active ? `border-[${accent}]/60` : "border-white/20"}`}
              style={{ color: accent, borderColor: `${accent}66` }}>
          Open queue →
        </span>
      </div>
    </button>
  );
}
